import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutDashboard, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

export const NAV = [
  ["Home", "/"], ["All Brands", "/brands"], ["Categories", "/categories"], ["Recently Updated", "/recent"], ["About", "/about"],
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export function Navbar() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);

  const isActive = (to) => (to === "/" ? pathname === "/" : pathname.startsWith(to));
  const AdminIcon = LayoutDashboard;

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-3 pt-3 md:px-6 md:pt-4" data-testid="site-navbar">
      <nav className={cn(
        "mx-auto flex max-w-7xl items-center justify-between rounded-full border px-4 py-2.5 transition-[background-color,box-shadow,border-color] duration-500 md:px-5",
        scrolled ? "ph2-glass border-ph-line shadow-[0_20px_50px_-30px_rgba(16,22,37,0.6)]" : "border-transparent bg-ph-bg/40 backdrop-blur-md",
      )}>
        <Logo tagline={false} />
        <div className="hidden items-center gap-1 lg:flex">
          {NAV.map(([label, to]) => (
            <NavLink key={to} to={to} data-testid={`nav-${slug(label)}`} className="relative rounded-full px-4 py-2 text-sm font-medium text-ph-muted transition-colors hover:text-ph-ink">
              {isActive(to) && <motion.span layoutId="nav-pill" className="absolute inset-0 rounded-full bg-ph-raised" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
              <span className={cn("relative", isActive(to) && "text-ph-ink")}>{label}</span>
            </NavLink>
          ))}
          {user && (
            <Link to="/admin" data-testid="nav-admin-dashboard" className="ml-3 inline-flex items-center gap-2 rounded-full bg-ph-lime px-5 py-2.5 text-sm font-semibold text-ph-navy transition-[box-shadow,transform] duration-300 hover:scale-[1.03] hover:shadow-[0_0_28px_rgba(182,255,92,0.45)]">
              <AdminIcon className="h-4 w-4" /> Dashboard
            </Link>
          )}
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button type="button" aria-label="Open menu" data-testid="nav-mobile-menu-btn" className="flex h-10 w-10 items-center justify-center rounded-full bg-ph-raised text-ph-ink lg:hidden">
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[86vw] max-w-sm border-l border-ph-line bg-ph-bg p-8" data-testid="nav-mobile-sheet">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <Logo />
            <div className="mt-12 flex flex-col gap-1">
              {NAV.map(([label, to], i) => (
                <motion.div key={to} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <Link to={to} data-testid={`mobile-nav-${slug(label)}`} className={cn("block py-3 font-display text-3xl font-extrabold tracking-tight", isActive(to) ? "text-ph-accent" : "text-ph-ink")}>
                    {label}
                  </Link>
                </motion.div>
              ))}
            </div>
            {user && (
              <Link to="/admin" data-testid="mobile-nav-admin-dashboard" className="mt-10 inline-flex items-center gap-2 rounded-full bg-ph-lime px-6 py-3 text-sm font-semibold text-ph-navy">
                <AdminIcon className="h-4 w-4" /> Dashboard
              </Link>
            )}
          </SheetContent>
        </Sheet>
      </nav>
    </header>
  );
}
